
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const Skills = () => {
  const { elementRef, isVisible } = useScrollAnimation();

  const skillCategories = [
    {
      title: 'Agricultural Expertise',
      icon: 'ri-plant-line',
      color: 'green',
      skills: [
        'Sustainable farming practices',
        'Agricultural project coordination',
        'Technical consultation and support',
        'Crop monitoring and management',
        'Soil testing and analysis',
        'Nutrient management and fertilization',
        'Feeding and grazing systems',
        'Farm operations and management',
        'Irrigation system management'
      ]
    },
    {
      title: 'Technical Skills',
      icon: 'ri-computer-line',
      color: 'blue',
      skills: [
        'Microsoft Office Suite (Word, Excel, PowerPoint)',
        'Bookkeeping (Pastel, Sage)',
        'Database management',
        'Record-keeping',
        'Data capture',
        'Financial records management',
        'Internal databases management'
      ]
    },
    {
      title: 'Professional Skills',
      icon: 'ri-user-line',
      color: 'amber',
      skills: [
        'Team player',
        'Adaptability',
        'Critical thinking',
        'Leadership',
        'Organizational skills',
        'Problem-solving',
        'Communication',
        'Proactive approach'
      ]
    },
    {
      title: 'Project Management',
      icon: 'ri-briefcase-line',
      color: 'purple',
      skills: [
        'Project coordination',
        'Meeting organization and scheduling',
        'Agenda preparation',
        'Office filing systems',
        'Training workshops organization',
        'Progress monitoring',
        'Compliance management',
        'Administrative support'
      ]
    }
  ];

  return (
    <section id="skills" className="py-20 bg-gradient-to-br from-green-50 to-amber-50 relative overflow-hidden">
      <div className="absolute inset-0 bg-pattern opacity-20"></div>
      
      <div className="relative z-10 max-w-6xl mx-auto px-6">
        <div ref={elementRef} className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'animate-fade-in-up' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-4xl md:text-6xl font-bold mb-6 gradient-earth font-serif">Skills & Expertise</h2>
          <div className="w-24 h-1 bg-gradient-to-r from-green-600 to-amber-600 mx-auto rounded-full"></div>
          <p className="text-lg text-gray-600 mt-6 max-w-2xl mx-auto">
            Comprehensive skill set developed through education and professional experience in agriculture and administration
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {skillCategories.map((category, categoryIndex) => (
            <div 
              key={categoryIndex}
              className={`transition-all duration-1200 ${isVisible ? 'animate-fade-in-up' : 'opacity-0 translate-y-8'}`}
              style={{ animationDelay: `${categoryIndex * 0.2}s` }}
            >
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl hover-lift card-hover h-full">
                <div className="flex items-center mb-6">
                  <div className={`w-12 h-12 bg-gradient-to-br from-${category.color}-400 to-${category.color}-600 rounded-xl flex items-center justify-center text-white text-xl mr-4`}>
                    <i className={category.icon}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 font-serif">{category.title}</h3>
                </div>
                
                <div className="space-y-3">
                  {category.skills.map((skill, skillIndex) => (
                    <div 
                      key={skillIndex}
                      className="flex items-center space-x-3 group"
                    >
                      <div className={`w-2 h-2 bg-${category.color}-500 rounded-full group-hover:scale-125 transition-transform duration-300`}></div>
                      <span className="text-gray-700 group-hover:text-gray-900 transition-colors duration-300 font-medium">
                        {skill}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Certifications Section */}
        <div className={`mt-16 transition-all duration-1200 ${isVisible ? 'animate-fade-in-up' : 'opacity-0 translate-y-8'}`}>
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-gray-800 font-serif mb-4">Education & Certifications</h3>
              <div className="w-16 h-1 bg-gradient-to-r from-green-600 to-amber-600 mx-auto rounded-full"></div>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-gradient-to-br from-green-50 to-amber-50 rounded-xl">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-amber-500 rounded-full flex items-center justify-center text-white text-2xl mx-auto mb-4">
                  <i className="ri-graduation-cap-line"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-800 mb-2">Bachelor of Agriculture Honours</h4>
                <p className="text-gray-600">University of KwaZulu Natal</p>
                <p className="text-sm text-gray-500 mt-1">2021</p>
              </div>
              
              <div className="text-center p-6 bg-gradient-to-br from-green-50 to-amber-50 rounded-xl">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-amber-500 rounded-full flex items-center justify-center text-white text-2xl mx-auto mb-4">
                  <i className="ri-book-line"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-800 mb-2">Bachelor of Agriculture</h4>
                <p className="text-gray-600">University of KwaZulu Natal</p>
                <p className="text-sm text-gray-500 mt-1">2020</p>
              </div>
              
              <div className="text-center p-6 bg-gradient-to-br from-green-50 to-amber-50 rounded-xl">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-amber-500 rounded-full flex items-center justify-center text-white text-2xl mx-auto mb-4">
                  <i className="ri-award-line"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-800 mb-2">National Senior Certificate</h4>
                <p className="text-gray-600">Rajasthorpe Secondary School</p>
                <p className="text-sm text-gray-500 mt-1">2015</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;
